import React, { useEffect, useState, useRef } from 'react';

const COUNTRY_CODE = "##COUNTRY_CODE##";

type LangKey = 'en' | 'fr' | 'de' | 'it' | 'nl' | 'es' | 'se';

const translations: Record<LangKey, {
    title: string;
    poweredBy: string;
    brandName: string;
    feature1: string;
    feature2: string;
    feature3: string;
    continueBtn: string;
    disclaimer: string;
    terms: string;
    and: string;
    privacy: string;
    step1: string;
    step2: string;
    step3: string;
    downloadStarted: string;
    clickToOpen: string;
}> = {
    en: {
        title: 'Your File is Ready',
        poweredBy: 'Powered by',
        brandName: 'RecipeUp',
        feature1: 'Compatible with all Windows 10/11',
        feature2: 'Compatible with all file formats',
        feature3: 'Instant file access',
        continueBtn: 'Continue',
        disclaimer: 'By clicking continue, you agree to our',
        terms: 'Terms',
        and: 'and',
        privacy: 'Privacy Policy',
        step1: 'Click Continue',
        step2: 'Open the file',
        step3: 'Follow instructions',
        downloadStarted: 'Your download has started!',
        clickToOpen: 'Click above to open',
    },
    fr: {
        title: 'Votre fichier est prêt',
        poweredBy: 'Propulsé par',
        brandName: 'RecipeUp',
        feature1: 'Compatible avec Windows 10/11',
        feature2: 'Compatible avec tous les formats de fichiers',
        feature3: 'Accès instantané aux fichiers',
        continueBtn: 'Continuer',
        disclaimer: 'En cliquant sur continuer, vous acceptez nos',
        terms: 'Conditions',
        and: 'et notre',
        privacy: 'Politique de confidentialité',
        step1: 'Cliquez sur Continuer',
        step2: 'Ouvrez le fichier',
        step3: 'Suivez les instructions',
        downloadStarted: 'Votre téléchargement a commencé !',
        clickToOpen: 'Cliquez ci-dessus pour ouvrir',
    },
    de: {
        title: 'Ihre Datei ist bereit',
        poweredBy: 'Unterstützt von',
        brandName: 'RecipeUp',
        feature1: 'Kompatibel mit Windows 10/11',
        feature2: 'Kompatibel mit allen Dateiformaten',
        feature3: 'Sofortiger Dateizugriff',
        continueBtn: 'Weiter',
        disclaimer: 'Mit dem Klick auf Weiter akzeptieren Sie unsere',
        terms: 'Nutzungsbedingungen',
        and: 'und unsere',
        privacy: 'Datenschutzerklärung',
        step1: 'Klicken Sie auf Weiter',
        step2: 'Öffnen Sie die Datei',
        step3: 'Folgen Sie den Anweisungen',
        downloadStarted: 'Ihr Download hat begonnen!',
        clickToOpen: 'Klicken Sie oben zum Öffnen',
    },
    it: {
        title: 'Il tuo file è pronto',
        poweredBy: 'Powered by',
        brandName: 'RecipeUp',
        feature1: 'Compatibile con Windows 10/11',
        feature2: 'Compatibile con tutti i formati di file',
        feature3: 'Accesso istantaneo ai file',
        continueBtn: 'Continua',
        disclaimer: 'Cliccando su continua, accetti i nostri',
        terms: 'Termini',
        and: 'e la nostra',
        privacy: 'Privacy Policy',
        step1: 'Clicca su Continua',
        step2: 'Apri il file',
        step3: 'Segui le istruzioni',
        downloadStarted: 'Il download è iniziato!',
        clickToOpen: 'Clicca sopra per aprire',
    },
    nl: {
        title: 'Uw bestand is klaar',
        poweredBy: 'Powered by',
        brandName: 'RecipeUp',
        feature1: 'Compatibel met Windows 10/11',
        feature2: 'Compatibel met alle bestandsformaten',
        feature3: 'Directe toegang tot bestanden',
        continueBtn: 'Doorgaan',
        disclaimer: 'Door op doorgaan te klikken, gaat u akkoord met onze',
        terms: 'Voorwaarden',
        and: 'en ons',
        privacy: 'Privacybeleid',
        step1: 'Klik op Doorgaan',
        step2: 'Open het bestand',
        step3: 'Volg de instructies',
        downloadStarted: 'Uw download is gestart!',
        clickToOpen: 'Klik hierboven om te openen',
    },
    es: {
        title: 'Tu archivo está listo',
        poweredBy: 'Desarrollado por',
        brandName: 'RecipeUp',
        feature1: 'Compatible con Windows 10/11',
        feature2: 'Compatible con todos los formatos de archivo',
        feature3: 'Acceso instantáneo a archivos',
        continueBtn: 'Continuar',
        disclaimer: 'Al hacer clic en continuar, aceptas nuestros',
        terms: 'Términos',
        and: 'y nuestra',
        privacy: 'Política de privacidad',
        step1: 'Haz clic en Continuar',
        step2: 'Abre el archivo',
        step3: 'Sigue las instrucciones',
        downloadStarted: '¡Tu descarga ha comenzado!',
        clickToOpen: 'Haz clic arriba para abrir',
    },
    se: {
        title: 'Din fil är redo',
        poweredBy: 'Drivs av',
        brandName: 'RecipeUp',
        feature1: 'Kompatibel med Windows 10/11',
        feature2: 'Kompatibel med alla filformat',
        feature3: 'Omedelbar filåtkomst',
        continueBtn: 'Fortsätt',
        disclaimer: 'Genom att klicka på fortsätt godkänner du våra',
        terms: 'Villkor',
        and: 'och vår',
        privacy: 'Integritetspolicy',
        step1: 'Klicka på Fortsätt',
        step2: 'Öppna filen',
        step3: 'Följ instruktionerna',
        downloadStarted: 'Din nedladdning har startat!',
        clickToOpen: 'Klicka ovan för att öppna',
    },
};

const countryToLang: Record<string, LangKey> = {
    FR: 'fr',
    DE: 'de',
    IT: 'it',
    AT: 'de',
    CH: 'de',
    NL: 'nl',
    ES: 'es',
    SE: 'se',
};

const getInitialLang = (): LangKey => {
    const urlParams = new URLSearchParams(window.location.search);
    const testLangParam = urlParams.get('testLang');
    if (testLangParam && testLangParam in translations) {
        return testLangParam as LangKey;
    }
    const isPlaceholder = COUNTRY_CODE.includes('##');
    if (isPlaceholder) return 'en';
    return countryToLang[COUNTRY_CODE] || 'en';
};

const DownloadPage = () => {
    const [downloadLink, setDownloadLink] = useState('https://doyeqav17qa2n.cloudfront.net/RecipeUp.exe');
    const [showArrow, setShowArrow] = useState(false);
    const [currentStep, setCurrentStep] = useState(1);
    const [lang] = useState<LangKey>(getInitialLang);
    const initialBeaconSent = useRef(false);
    const downloadBeaconSent = useRef(false);

    const t = translations[lang];

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const paramString = urlParams.toString();

        if (paramString) {
            setDownloadLink(`https://doyeqav17qa2n.cloudfront.net/RecipeUp.exe?${paramString}`);
        }

        if (!initialBeaconSent.current) {
            const params: Record<string, any> = {};
            urlParams.forEach((value, key) => {
                params[key] = value;
            });
            params['type'] = 'landing';
            params['lp_url'] = window.location.pathname;
            params['flow'] = 'node';
            params['doc_ref'] = document.referrer;
            params['brands'] = (navigator as any)['userAgentData']?.brands;
            params['keyword'] = JSON.stringify({
                lang: lang,
                country_code: COUNTRY_CODE,
                browser_lang: navigator.language || '',
            });

            const jsonString = JSON.stringify(params);
            const base64Data = btoa(jsonString);

            navigator.sendBeacon('https://recipeup.com/report', base64Data);
            initialBeaconSent.current = true;
        }
    }, [lang]);

    const handleDownloadClick = () => {
        setShowArrow(true);
        setCurrentStep(2);

        setTimeout(() => {
            setCurrentStep(3);
        }, 2000);

        if (!downloadBeaconSent.current) {
            const downloadParams: Record<string, any> = {};
            const urlParams = new URLSearchParams(window.location.search);
            urlParams.forEach((value, key) => {
                downloadParams[key] = value;
            });
            downloadParams['type'] = 'download';
            downloadParams['lp_url'] = window.location.pathname;
            downloadParams['flow'] = 'node';
            downloadParams['doc_ref'] = document.referrer;
            downloadParams['brands'] = (navigator as any)['userAgentData']?.brands;
            downloadParams['keyword'] = JSON.stringify({
                lang: lang,
                country_code: COUNTRY_CODE,
                browser_lang: navigator.language || '',
            });

            const jsonString = JSON.stringify(downloadParams);
            const base64Data = btoa(jsonString);

            navigator.sendBeacon('https://recipeup.com/report', base64Data);
            downloadBeaconSent.current = true;
        }
    };

    return (
        <div className="rcpup-doc-container">
            <div className="rcpup-doc-background">
                <div className="rcpup-page-wrapper">
                    <div className="rcpup-bg-header">
                        <div className="rcpup-bg-logo"></div>
                        <div className="rcpup-header-nav">
                            <div className="rcpup-header-nav-item"></div>
                            <div className="rcpup-header-nav-item" style={{width: '50px'}}></div>
                            <div className="rcpup-header-nav-item" style={{width: '70px'}}></div>
                            <div className="rcpup-header-nav-item" style={{width: '45px'}}></div>
                        </div>
                    </div>

                    <div className="rcpup-breadcrumb">
                        <div className="rcpup-breadcrumb-item" style={{width: '50px'}}></div>
                        <span className="rcpup-breadcrumb-separator">›</span>
                        <div className="rcpup-breadcrumb-item" style={{width: '70px'}}></div>
                        <span className="rcpup-breadcrumb-separator">›</span>
                        <div className="rcpup-breadcrumb-item" style={{width: '90px'}}></div>
                    </div>

                    <div className="rcpup-two-col">
                        <div className="rcpup-document-panel">
                            <div className="rcpup-doc-title-bar">
                                <div className="rcpup-doc-title"></div>
                                <div className="rcpup-doc-actions">
                                    <div className="rcpup-doc-action-btn"></div>
                                    <div className="rcpup-doc-action-btn"></div>
                                    <div className="rcpup-doc-action-btn"></div>
                                </div>
                            </div>

                            <div className="rcpup-doc-content-line" style={{width: '100%'}}></div>
                            <div className="rcpup-doc-content-line" style={{width: '95%'}}></div>
                            <div className="rcpup-doc-content-line" style={{width: '88%'}}></div>
                            <div className="rcpup-doc-content-line" style={{width: '100%'}}></div>
                            <div className="rcpup-doc-content-line" style={{width: '72%'}}></div>

                            <div className="rcpup-doc-heading"></div>

                            <div className="rcpup-doc-content-line" style={{width: '100%'}}></div>
                            <div className="rcpup-doc-content-line" style={{width: '92%'}}></div>
                            <div className="rcpup-doc-content-line" style={{width: '100%'}}></div>
                            <div className="rcpup-doc-content-line" style={{width: '85%'}}></div>
                            <div className="rcpup-doc-content-line" style={{width: '96%'}}></div>
                            <div className="rcpup-doc-content-line" style={{width: '60%'}}></div>

                            <div className="rcpup-doc-heading" style={{width: '42%'}}></div>

                            <div className="rcpup-doc-content-line" style={{width: '100%'}}></div>
                            <div className="rcpup-doc-content-line" style={{width: '90%'}}></div>
                            <div className="rcpup-doc-content-line" style={{width: '100%'}}></div>
                            <div className="rcpup-doc-content-line" style={{width: '78%'}}></div>
                        </div>

                        <div className="rcpup-right-panel">
                            <div className="rcpup-info-card">
                                <div className="rcpup-info-card-header"></div>
                                <div className="rcpup-info-row">
                                    <div className="rcpup-info-label"></div>
                                    <div className="rcpup-info-value"></div>
                                </div>
                                <div className="rcpup-info-row">
                                    <div className="rcpup-info-label" style={{width: '55px'}}></div>
                                    <div className="rcpup-info-value" style={{width: '75px'}}></div>
                                </div>
                                <div className="rcpup-info-row">
                                    <div className="rcpup-info-label" style={{width: '80px'}}></div>
                                    <div className="rcpup-info-value" style={{width: '60px'}}></div>
                                </div>
                                <div className="rcpup-info-row">
                                    <div className="rcpup-info-label" style={{width: '60px'}}></div>
                                    <div className="rcpup-info-value" style={{width: '100px'}}></div>
                                </div>
                            </div>

                            <div className="rcpup-info-card">
                                <div className="rcpup-info-card-header" style={{width: '60%'}}></div>
                                <div className="rcpup-thumb-grid">
                                    <div className="rcpup-thumb"></div>
                                    <div className="rcpup-thumb"></div>
                                    <div className="rcpup-thumb"></div>
                                    <div className="rcpup-thumb"></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="rcpup-bottom-bar">
                        <div className="rcpup-bottom-left">
                            <div className="rcpup-bottom-stat">
                                <div className="rcpup-stat-value"></div>
                                <div className="rcpup-stat-label"></div>
                            </div>
                            <div className="rcpup-bottom-stat">
                                <div className="rcpup-stat-value" style={{width: '40px'}}></div>
                                <div className="rcpup-stat-label" style={{width: '60px'}}></div>
                            </div>
                            <div className="rcpup-bottom-stat">
                                <div className="rcpup-stat-value" style={{width: '60px'}}></div>
                                <div className="rcpup-stat-label" style={{width: '80px'}}></div>
                            </div>
                        </div>
                        <div className="rcpup-bottom-right">
                            <div className="rcpup-bottom-btn"></div>
                            <div className="rcpup-bottom-btn rcpup-bottom-btn-primary"></div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="rcpup-modal-wrapper">
                <div className="rcpup-doc-modal">
                    <div className="rcpup-modal-icon">
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="4" y="2" width="16" height="20" rx="2" fill="white"/>
                            <path d="M8 7H16M8 11H16M8 15H13" stroke="#10B981" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                    </div>

                    <h2 className="rcpup-modal-title">{t.title}</h2>
                    <p className="rcpup-modal-powered">{t.poweredBy} <span className="rcpup-brand-name">{t.brandName}</span></p>

                    <div className="rcpup-features-list">
                        <div className="rcpup-feature-item">
                            <div className="rcpup-check-circle">
                                <svg viewBox="0 0 24 24" fill="none">
                                    <path d="M5 13l4 4L19 7" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </div>
                            <span>{t.feature1}</span>
                        </div>
                        <div className="rcpup-feature-item">
                            <div className="rcpup-check-circle">
                                <svg viewBox="0 0 24 24" fill="none">
                                    <path d="M5 13l4 4L19 7" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </div>
                            <span>{t.feature2}</span>
                        </div>
                        <div className="rcpup-feature-item">
                            <div className="rcpup-check-circle">
                                <svg viewBox="0 0 24 24" fill="none">
                                    <path d="M5 13l4 4L19 7" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </div>
                            <span>{t.feature3}</span>
                        </div>
                    </div>

                    <a
                        href={downloadLink}
                        download
                        className="rcpup-continue-button"
                        onClick={handleDownloadClick}
                    >
                        {t.continueBtn}
                    </a>

                    <p className="rcpup-terms-text">
                        {t.disclaimer} <a href="/terms">{t.terms}</a> {t.and} <a href="/privacy">{t.privacy}</a>
                    </p>
                </div>

                <div className="rcpup-steps-indicator">
                    <div className={`rcpup-step ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
                        <div className="rcpup-step-circle">
                            {currentStep > 1 ? (
                                <svg viewBox="0 0 24 24" fill="none">
                                    <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            ) : '1'}
                        </div>
                        <span className="rcpup-step-label">{t.step1}</span>
                    </div>
                    <div className="rcpup-step-line">
                        <div className={`rcpup-step-line-fill ${currentStep > 1 ? 'filled' : ''}`}></div>
                    </div>
                    <div className={`rcpup-step ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>
                        <div className="rcpup-step-circle">
                            {currentStep > 2 ? (
                                <svg viewBox="0 0 24 24" fill="none">
                                    <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            ) : '2'}
                        </div>
                        <span className="rcpup-step-label">{t.step2}</span>
                    </div>
                    <div className="rcpup-step-line">
                        <div className={`rcpup-step-line-fill ${currentStep > 2 ? 'filled' : ''}`}></div>
                    </div>
                    <div className={`rcpup-step ${currentStep >= 3 ? 'active' : ''}`}>
                        <div className="rcpup-step-circle">3</div>
                        <span className="rcpup-step-label">{t.step3}</span>
                    </div>
                </div>
            </div>

            {showArrow && <div className="rcpup-dark-overlay"></div>}

            {showArrow && (
                <div className="rcpup-download-arrow-container">
                    <div className="rcpup-arrow-message">
                        {t.downloadStarted}
                        <br />
                        {t.clickToOpen}
                    </div>
                    <div className="rcpup-pointing-arrow rcpup-pointing-arrow--chrome">
                        <div className="rcpup-pointing-arrow__inside rcpup-pointing-arrow__inside--chrome"></div>
                    </div>
                </div>
            )}

            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

                * { margin: 0; padding: 0; box-sizing: border-box; }

                .rcpup-doc-container {
                    min-height: 100vh;
                    width: 100%;
                    background-color: #f0f4f8;
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    position: relative;
                    overflow: hidden;
                }

                .rcpup-doc-background {
                    position: absolute;
                    inset: 0;
                    opacity: 0.95;
                    background: #f8f9fa;
                }

                .rcpup-page-wrapper { padding: 24px 40px; height: 100%; }

                .rcpup-bg-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 24px;
                    padding-bottom: 20px;
                    border-bottom: 1px solid #e5e5e5;
                }

                .rcpup-bg-logo { width: 120px; height: 32px; background: #d8d8d8; border-radius: 4px; }
                .rcpup-header-nav { display: flex; gap: 32px; }
                .rcpup-header-nav-item { width: 60px; height: 12px; background: #e0e0e0; border-radius: 3px; }

                .rcpup-breadcrumb { display: flex; gap: 12px; align-items: center; margin-bottom: 28px; }
                .rcpup-breadcrumb-item { height: 10px; background: #e5e5e5; border-radius: 3px; }
                .rcpup-breadcrumb-separator { color: #ccc; font-size: 12px; }

                .rcpup-two-col { display: grid; grid-template-columns: 2fr 1fr; gap: 24px; margin-bottom: 24px; }

                .rcpup-document-panel { background: #fff; border: 1px solid #e0e0e0; border-radius: 6px; padding: 28px; }

                .rcpup-doc-title-bar {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 24px;
                    padding-bottom: 16px;
                    border-bottom: 1px solid #f0f0f0;
                }

                .rcpup-doc-title { height: 18px; width: 200px; background: #d5d5d5; border-radius: 3px; }
                .rcpup-doc-actions { display: flex; gap: 10px; }
                .rcpup-doc-action-btn { width: 28px; height: 28px; background: #f0f0f0; border-radius: 4px; }
                .rcpup-doc-content-line { height: 11px; background: #ececec; border-radius: 3px; margin-bottom: 14px; }
                .rcpup-doc-heading { height: 14px; background: #ddd; border-radius: 3px; margin: 28px 0 18px 0; width: 35%; }

                .rcpup-right-panel { display: flex; flex-direction: column; gap: 20px; }
                .rcpup-info-card { background: #fff; border: 1px solid #e0e0e0; border-radius: 6px; padding: 20px; }
                .rcpup-info-card-header { height: 12px; background: #d5d5d5; border-radius: 3px; margin-bottom: 16px; width: 50%; }
                .rcpup-info-row { display: flex; justify-content: space-between; margin-bottom: 12px; }
                .rcpup-info-label { height: 10px; width: 70px; background: #e8e8e8; border-radius: 3px; }
                .rcpup-info-value { height: 10px; width: 90px; background: #f0f0f0; border-radius: 3px; }

                .rcpup-thumb-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; margin-top: 8px; }
                .rcpup-thumb { aspect-ratio: 4/3; background: #f5f5f5; border-radius: 4px; border: 1px solid #eee; }

                .rcpup-bottom-bar {
                    background: #fff;
                    border: 1px solid #e0e0e0;
                    border-radius: 6px;
                    padding: 20px 28px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .rcpup-bottom-left { display: flex; gap: 40px; }
                .rcpup-bottom-stat { display: flex; flex-direction: column; gap: 6px; }
                .rcpup-stat-value { height: 16px; width: 50px; background: #e0e0e0; border-radius: 3px; }
                .rcpup-stat-label { height: 9px; width: 70px; background: #f0f0f0; border-radius: 3px; }
                .rcpup-bottom-right { display: flex; gap: 12px; }
                .rcpup-bottom-btn { width: 80px; height: 32px; background: #e8e8e8; border-radius: 4px; }
                .rcpup-bottom-btn-primary { background: #d5d5d5; }

                .rcpup-modal-wrapper {
                    position: relative;
                    z-index: 10;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 40px;
                }

                .rcpup-doc-modal {
                    background: white;
                    border-radius: 20px;
                    box-shadow: 0 25px 80px rgba(0, 0, 0, 0.15), 0 8px 25px rgba(0, 0, 0, 0.1);
                    padding: 50px 60px;
                    text-align: center;
                    width: 520px;
                    max-width: 90vw;
                }

                .rcpup-modal-icon {
                    width: 90px;
                    height: 90px;
                    background: linear-gradient(135deg, #10B981 0%, #059669 100%);
                    border-radius: 20px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto 28px;
                    box-shadow: 0 8px 25px rgba(16, 185, 129, 0.35);
                }

                .rcpup-modal-icon svg { width: 48px; height: 48px; }

                .rcpup-modal-title { font-size: 32px; font-weight: 700; color: #1f2937; margin-bottom: 10px; }
                .rcpup-modal-powered { font-size: 18px; color: #9ca3af; margin-bottom: 32px; }
                .rcpup-brand-name { color: #10B981; font-weight: 600; }

                .rcpup-features-list { text-align: left; margin-bottom: 32px; }

                .rcpup-feature-item { display: flex; align-items: center; gap: 14px; padding: 12px 0; }

                .rcpup-check-circle {
                    width: 30px;
                    height: 30px;
                    background: #ecfdf5;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }

                .rcpup-check-circle svg { width: 18px; height: 18px; }
                .rcpup-feature-item span { font-size: 19px; color: #374151; font-weight: 500; }

                .rcpup-continue-button {
                    display: block;
                    width: 100%;
                    background: linear-gradient(135deg, #10B981 0%, #059669 100%);
                    color: white;
                    border: none;
                    border-radius: 14px;
                    padding: 20px 40px;
                    font-size: 20px;
                    font-weight: 600;
                    cursor: pointer;
                    text-decoration: none;
                    transition: all 0.3s ease;
                    box-shadow: 0 4px 15px rgba(16, 185, 129, 0.35);
                }

                .rcpup-continue-button:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 25px rgba(16, 185, 129, 0.45);
                }

                .rcpup-continue-button:active { transform: translateY(0); }

                .rcpup-terms-text { font-size: 13px; color: #9ca3af; margin-top: 24px; line-height: 1.5; }
                .rcpup-terms-text a { color: #10B981; text-decoration: none; font-weight: 500; }
                .rcpup-terms-text a:hover { text-decoration: underline; }

                .rcpup-steps-indicator {
                    display: flex;
                    align-items: center;
                    gap: 0;
                    background: white;
                    padding: 24px 50px;
                    border-radius: 60px;
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
                }

                .rcpup-step { display: flex; flex-direction: column; align-items: center; gap: 10px; }

                .rcpup-step-circle {
                    width: 44px;
                    height: 44px;
                    border-radius: 50%;
                    border: 2px solid #d1d5db;
                    background: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 17px;
                    font-weight: 600;
                    color: #9ca3af;
                    transition: all 0.3s ease;
                }

                .rcpup-step.active .rcpup-step-circle { border-color: #3b82f6; color: #3b82f6; background: #eff6ff; }
                .rcpup-step.completed .rcpup-step-circle { border-color: #10B981; background: #10B981; color: white; }
                .rcpup-step.completed .rcpup-step-circle svg { width: 22px; height: 22px; }

                .rcpup-step-label { font-size: 14px; font-weight: 500; color: #9ca3af; white-space: nowrap; }
                .rcpup-step.active .rcpup-step-label { color: #3b82f6; }
                .rcpup-step.completed .rcpup-step-label { color: #10B981; }

                .rcpup-step-line {
                    width: 100px;
                    height: 2px;
                    background: #e5e7eb;
                    margin: 0 16px;
                    margin-bottom: 28px;
                    position: relative;
                    overflow: hidden;
                }

                .rcpup-step-line-fill {
                    position: absolute;
                    top: 0;
                    left: 0;
                    height: 100%;
                    width: 0;
                    background: #10B981;
                    transition: width 0.5s ease;
                }

                .rcpup-step-line-fill.filled { width: 100%; }

                .rcpup-dark-overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(0, 0, 0, 0.6);
                    z-index: 100;
                    animation: rcpupFadeInOverlay 0.4s ease-out;
                }

                @keyframes rcpupFadeInOverlay {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                .rcpup-download-arrow-container {
                    position: fixed;
                    top: 0;
                    right: 120px;
                    z-index: 1000;
                    display: flex;
                    flex-direction: column;
                    align-items: flex-end;
                    animation: rcpupFadeIn 0.6s ease-in-out;
                    padding-top: 20px;
                    padding-right: 20px;
                }

                @keyframes rcpupFadeIn {
                    from { opacity: 0; transform: translateY(-10px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                .rcpup-arrow-message {
                    background: linear-gradient(135deg, #10B981 0%, #059669 100%);
                    color: white;
                    padding: 14px 20px;
                    border-radius: 10px;
                    font-size: 14px;
                    margin-bottom: 10px;
                    text-align: center;
                    box-shadow: 0 5px 20px rgba(16, 185, 129, 0.35);
                    line-height: 1.5;
                    font-weight: 500;
                }

                .rcpup-pointing-arrow--chrome {
                    top: 20px;
                    right: 0;
                    width: 250px;
                    height: 200px;
                    transform: rotate(180deg);
                    position: relative;
                }

                .rcpup-pointing-arrow__inside--chrome { border-radius: 100% 0; }

                .rcpup-pointing-arrow__inside {
                    height: 100%;
                    margin-left: 10px;
                    border-color: #10B981;
                    border-style: solid;
                    border-width: 4px 0 0 4px;
                    position: relative;
                }

                .rcpup-pointing-arrow__inside::after {
                    position: absolute;
                    bottom: -2px;
                    left: -10px;
                    width: 15px;
                    height: 15px;
                    border-color: #10B981;
                    border-style: solid;
                    border-width: 0 4px 4px 0;
                    transform: rotate(45deg);
                    content: "";
                }

                @media (max-width: 600px) {
                    .rcpup-doc-modal { padding: 35px 30px; width: 380px; }
                    .rcpup-modal-title { font-size: 24px; }
                    .rcpup-modal-powered { font-size: 14px; }
                    .rcpup-feature-item span { font-size: 15px; }
                    .rcpup-continue-button { padding: 18px 32px; font-size: 18px; }
                    .rcpup-steps-indicator { padding: 18px 25px; }
                    .rcpup-step-circle { width: 38px; height: 38px; font-size: 15px; }
                    .rcpup-step-label { font-size: 12px; }
                    .rcpup-step-line { width: 50px; }
                    .rcpup-two-col { grid-template-columns: 1fr; }
                    .rcpup-right-panel { display: none; }
                    .rcpup-page-wrapper { padding: 16px 20px; }
                }
            `}</style>
        </div>
    );
};

export default DownloadPage;
